import { ComponentProps, FC, ReactElement, useState } from "react";
import { Alert, Typography } from "antd";
import { EyeTwoTone, EyeInvisibleOutlined } from "@ant-design/icons";
import { Form, Input } from "formik-antd";
import * as _ from "lodash";
import ReCAPTCHA from "react-google-recaptcha";

import { classname, isProduction } from "@ctra/utils";

import { Button } from "../../../elements";
import styles from "./SignupForm.module.less";

const { Text } = Typography;

export interface SignupFormProps extends Omit<ComponentProps<typeof Form>, "disabled"> {
  /**
   * translated labels
   */
  labels: {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    password: string;
    confirmPassword: string;
    create: string;
    rejected?: string;
    eula: ReactElement;
  };
  /**
   * disabled state
   */
  disabled?: {
    email?: boolean;
  };
  /**
   * handle field blur
   * @param {string} fieldName
   */
  onFieldBlur: (fieldName: "email" | "password" | "confirmPassword" | "phoneNumber") => void;
  /**
   * loading state
   */
  isLoading?: boolean;
}

export const testIds = {
  emailField: "emailField",
  phoneNumberField: "phoneNumberField",
  firstNameField: "firstNameField",
  lastNameField: "lastNameField",
  passwordField: "passwordField",
  confirmPasswordField: "confirmPasswordField",
  create: "create",
  eula: "eula"
};

const reCAPTCHAkey = "6LcOlRwlAAAAAEgSXpU9QQgY0pKZaIFq0PYd6bjb";

/**
 * Sign up form component
 * @param {any} emailDisabled
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} email
 * @param {string} phoneNumber
 * @param {string} password
 * @param {string} confirmPassword
 * @param {string} create
 * @param {string | undefined} rejected
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>>} eula
 * @param {boolean | undefined} isLoading
 * @param {(fieldName: ("email" | "password" | "confirmPassword" | "phoneNumber")) => void} onFieldBlur
 * @param {string | undefined} className
 * @param {Pick<SignupFormProps & {children?: React.ReactNode | undefined}, "prefixCls" | "colon" | "name" | "layout" | "labelAlign" | "labelWrap" | "labelCol" | "wrapperCol" | "form" | "size" | "scrollToFirstError" | "requiredMark" | "hideRequiredMark" | "initialValues" | "children" | "component" | "fields" | "validateMessages" | "onValuesChange" | "onFieldsChange" | "onFinish" | "onFinishFailed" | "validateTrigger" | "preserve" | "acceptCharset" | "action" | "autoComplete" | "encType" | "method" | "noValidate" | "target" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "title" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture">} rest
 * @return {JSX.Element}
 * @constructor
 */
const SignupForm: FC<SignupFormProps> & { testIds: typeof testIds } = ({
  disabled: { email: emailDisabled } = {},
  labels: { firstName, lastName, email, phoneNumber, password, confirmPassword, create, rejected, eula },
  isLoading,
  onFieldBlur,
  className,
  ...rest
}) => {
  const [captchaVerified, setCaptchaVerified] = useState(!isProduction());

  return (
    <Form
      layout="vertical"
      className={classname("ctra-auth-SignupForm", styles.SignupForm, className)}
      {...rest}
    >
      <Input type="hidden" name="eulaAccepted" />
      <Form.Item name="firstName" label={firstName} hasFeedback={false}>
        <Input
          data-testid={testIds.firstNameField}
          disabled={isLoading}
          name="firstName"
          multiple={true}
          required={true}
        />
      </Form.Item>
      <Form.Item name="lastName" label={lastName} hasFeedback={false}>
        <Input
          data-testid={testIds.lastNameField}
          disabled={isLoading}
          name="lastName"
          multiple={true}
          required={true}
        />
      </Form.Item>
      <Form.Item name="email" label={email} hasFeedback={false}>
        <Input
          data-testid={testIds.emailField}
          disabled={isLoading || emailDisabled}
          type="email"
          name="email"
          multiple={true}
          required={true}
          onBlur={_.partial(onFieldBlur, "email")}
        />
      </Form.Item>
      <Form.Item name="phoneNumber" label={phoneNumber} hasFeedback={false}>
        <Input
          data-testid={testIds.phoneNumberField}
          disabled={isLoading}
          type="tel"
          name="phoneNumber"
          onBlur={_.partial(onFieldBlur, "phoneNumber")}
        />
      </Form.Item>
      <Form.Item name="password" label={password} hasFeedback={false}>
        <Input.Password
          data-testid={testIds.passwordField}
          disabled={isLoading}
          iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          name="password"
          required={true}
          onBlur={_.partial(onFieldBlur, "password")}
        />
      </Form.Item>
      <Form.Item name="confirmPassword" label={confirmPassword} hasFeedback={false}>
        <Input.Password
          data-testid={testIds.confirmPasswordField}
          disabled={isLoading}
          iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          name="confirmPassword"
          required={true}
          onBlur={_.partial(onFieldBlur, "confirmPassword")}
        />
      </Form.Item>
      <Form.Item name="eulaAccepted">
        <Text>{eula}</Text>
      </Form.Item>
      <ReCAPTCHA sitekey={reCAPTCHAkey} onChange={(token) => setCaptchaVerified(!!token)} />
      <Button
        block
        className={styles.Button}
        data-testid={testIds.create}
        type="primary"
        htmlType="submit"
        size="large"
        loading={isLoading}
        disabled={isLoading || !captchaVerified}
      >
        <strong>{create}</strong>
      </Button>
      {rejected && <Alert closable showIcon type="error" message={rejected} />}
    </Form>
  );
};

SignupForm.testIds = testIds;

export default SignupForm;
